import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { reportsAPI, suppliersAPI, categoriesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowLeft, Truck, Wallet, TrendingUp, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { DateRangeBar, ExportBar, KPICard, ReportLoader, ReportEmpty, ReportError, useCurrency, downloadCsv } from '../../components/reports/ReportKit';
import { printReportDocument } from '../../lib/printReport';

const PurchaseReport = () => {
    const { t, i18n } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const showCost = isAdmin();
    const fmtMoney = useCurrency();
    const [filters, setFilters] = useState({ start: null, end: null, supplier_id: 'all', category_id: 'all', warehouse: 'all' });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const pageSize = 50;
    const [suppliers, setSuppliers] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, c] = await Promise.all([
                    suppliersAPI.getAll().catch(() => ({ data: [] })),
                    categoriesAPI.getAll().catch(() => ({ data: [] })),
                ]);
                setSuppliers(s.data || []);
                setCategories(c.data || []);
        } catch (err) {
            // Non-fatal — filter dropdowns just render without labels.
            console.error('[PurchaseReport] failed to load filter data:', err?.response?.status || err?.message);
        }
        })();
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 90);
        setFilters((f) => ({ ...f, start, end }));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: pageSize };
            if (filters.start) params.start_date = format(filters.start, 'yyyy-MM-dd');
            if (filters.end) params.end_date = format(filters.end, 'yyyy-MM-dd');
            if (filters.supplier_id !== 'all') params.supplier_id = filters.supplier_id;
            if (filters.category_id !== 'all') params.category_id = filters.category_id;
            if (filters.warehouse !== 'all') params.warehouse = filters.warehouse;
            const { data: res } = await reportsAPI.purchaseAnalytics(params);
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [filters, page, t]);

    useEffect(() => { if (filters.start && filters.end) load(); }, [load, filters.start, filters.end]);

    const setPreset = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setFilters((f) => ({ ...f, start, end }));
        setPage(1);
    };

    const exportCsv = () => {
        if (!data?.rows?.length) return toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
        const columns = [
            { key: 'received_at', label: t('entities:reports.columns.date') },
            { key: 'batch_number', label: t('entities:batches.columns.batch') },
            { key: 'product_name', label: t('entities:products.columns.name') },
            { key: 'sku', label: t('entities:products.columns.sku') },
            { key: 'supplier_name', label: t('entities:suppliers.title') },
            { key: 'warehouse', label: t('entities:batches.columns.warehouse') },
            { key: 'quantity_received', label: t('entities:batches.dialog.fields.qty_received') },
            ...(showCost ? [
                { key: 'cost_price', label: t('entities:products.dialog.cost_price') },
                { key: 'total_cost', label: t('entities:reports.purchase.total_cost', { defaultValue: 'Total Cost' }) },
            ] : []),
            { key: 'status', label: t('common:common.status') },
            { key: 'expiry_date', label: t('entities:batches.columns.expiry') },
        ];
        downloadCsv(`purchase-report-${format(filters.start, 'yyyy-MM-dd')}_${format(filters.end, 'yyyy-MM-dd')}.csv`, columns, data.rows);
        toast.success(t('entities:reports.toasts.exported'));
    };

    const handlePrint = () => {
        if (!data?.rows?.length) {
            toast.error(t('entities:reports.toasts.nothing_to_export', { defaultValue: 'Nothing to export' }));
            return;
        }
        const lang = i18n.language || 'fr';
        const moneyTxt = (v) => fmtMoney(v).replace(/\u00a0/g, ' ');
        const period = (filters.start && filters.end)
            ? `${format(filters.start, 'dd MMM yyyy')} — ${format(filters.end, 'dd MMM yyyy')}`
            : '';
        const k = data?.kpis || {};
        const rows = data.rows.map((r) => `
            <tr>
                <td>${(r.received_at || '').slice(0, 10)}</td>
                <td style="font-family:'Courier New',monospace;">${r.batch_number || '—'}</td>
                <td>${r.product_name || '—'}</td>
                <td>${r.supplier_name || '—'}</td>
                <td class="num">${r.quantity_received ?? 0}</td>
                ${showCost ? `<td class="num">${moneyTxt(r.cost_price || 0)}</td>` : ''}
                ${showCost ? `<td class="num" style="font-weight:600;">${moneyTxt(r.total_cost || 0)}</td>` : ''}
                <td>${r.expiry_date || '—'}</td>
            </tr>
        `).join('');
        const metaHtml = `
            <div class="kpi-strip">
                <div class="kpi"><span class="k">${lang.startsWith('fr') ? 'Lots reçus' : 'Batches received'}</span><span class="v">${k.batches_count ?? 0}</span></div>
                ${showCost ? `<div class="kpi accent"><span class="k">${lang.startsWith('fr') ? 'Coût total' : 'Total cost'}</span><span class="v">${moneyTxt(k.total_cost)}</span></div>` : ''}
                <div class="kpi success"><span class="k">${lang.startsWith('fr') ? 'Unités reçues' : 'Units received'}</span><span class="v">${k.total_units ?? 0}</span></div>
            </div>
        `;
        const contentHtml = `
            <table class="report-table">
                <thead><tr>
                    <th>${lang.startsWith('fr') ? 'Reçu le' : 'Received'}</th>
                    <th>${lang.startsWith('fr') ? 'Lot N°' : 'Batch N°'}</th>
                    <th>${lang.startsWith('fr') ? 'Produit' : 'Product'}</th>
                    <th>${lang.startsWith('fr') ? 'Fournisseur' : 'Supplier'}</th>
                    <th class="num">${lang.startsWith('fr') ? 'Qté' : 'Qty'}</th>
                    ${showCost ? `<th class="num">${lang.startsWith('fr') ? 'P.A.' : 'Cost'}</th>` : ''}
                    ${showCost ? `<th class="num">${lang.startsWith('fr') ? 'Total' : 'Total'}</th>` : ''}
                    <th>${lang.startsWith('fr') ? 'Péremption' : 'Expiry'}</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
        `;
        printReportDocument({
            title: lang.startsWith('fr') ? 'Rapport d\'Achats' : 'Purchase Report',
            subtitle: period,
            metaHtml,
            contentHtml,
            language: lang,
        });
    };

    const k = data?.kpis;
    const total = data?.pagination?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const statusBadge = (s) => {
        const map = {
            active: 'bg-[#2A9D8F]/15 text-[#2A9D8F]',
            depleted: 'bg-gray-200 text-gray-600',
            recalled: 'bg-[#E76F51]/20 text-[#E76F51]',
            expired: 'bg-[#E76F51]/40 text-[#E76F51]',
        };
        return <Badge className={`${map[s] || map.active} font-medium text-[10px]`}>{t(`entities:batches.statuses.${s}`, { defaultValue: s })}</Badge>;
    };

    return (
        <div data-testid="purchase-report-page" className="space-y-6 animate-fadeIn">
            <div className="flex items-center justify-between no-print">
                <div>
                    <Link to="/reports" className="text-xs text-gray-500 hover:text-[#C08478] flex items-center gap-1 mb-2">
                        <ArrowLeft size={12} /> {t('entities:reports.hub.title')}
                    </Link>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:reports.hub.cards.purchase.title')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('entities:reports.hub.cards.purchase.description')}</p>
                </div>
                <ExportBar onCsv={exportCsv} onPrint={handlePrint} disabled={!data?.rows?.length} />
            </div>

            <DateRangeBar
                start={filters.start}
                end={filters.end}
                onChange={({ start, end }) => { setFilters((f) => ({ ...f, start, end })); setPage(1); }}
                onPreset={setPreset}
            >
                <Select value={filters.supplier_id} onValueChange={(v) => { setFilters((f) => ({ ...f, supplier_id: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-44" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:batches.filters.all_suppliers')}</SelectItem>
                        {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                </Select>
                <Select value={filters.category_id} onValueChange={(v) => { setFilters((f) => ({ ...f, category_id: v })); setPage(1); }}>
                    <SelectTrigger className="h-9 w-44" data-testid="filter-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('entities:products.filters.all_categories')}</SelectItem>
                        {categories.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                </Select>
            </DateRangeBar>

            {loading && <ReportLoader />}
            {error && !loading && <ReportError message={error} onRetry={load} />}

            {!loading && !error && k && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {showCost && <KPICard testId="kpi-total-cost" icon={Wallet} accent="default" label={t('entities:reports.purchase.kpi.total_cost', { defaultValue: 'Total purchases' })} value={fmtMoney(k.total_purchase_cost)} hint={`${k.total_purchase_qty} ${t('entities:expiry.units_suffix')}`} />}
                        {showCost && <KPICard testId="kpi-avg-cost" icon={TrendingUp} accent="info" label={t('entities:reports.purchase.kpi.avg_unit_cost', { defaultValue: 'Avg unit cost' })} value={fmtMoney(k.avg_unit_cost)} />}
                        <KPICard testId="kpi-suppliers" icon={Truck} accent="accent" label={t('entities:reports.purchase.kpi.suppliers', { defaultValue: 'Suppliers' })} value={k.supplier_count} />
                        <KPICard testId="kpi-batches" icon={Truck} accent="success" label={t('entities:reports.purchase.kpi.batches', { defaultValue: 'Batches received' })} value={k.batch_count} />
                        <KPICard testId="kpi-recalls" icon={AlertTriangle} accent="danger" label={t('entities:reports.purchase.kpi.recalls', { defaultValue: 'Recalls' })} value={k.recall_count} hint={showCost ? fmtMoney(k.return_value) : undefined} />
                    </div>

                    {/* Top suppliers chart */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm">{t('entities:reports.purchase.top_suppliers', { defaultValue: 'Top suppliers' })}</CardTitle></CardHeader>
                        <CardContent>
                            {data.charts.top_suppliers.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={data.charts.top_suppliers} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                                        <XAxis type="number" tick={{ fontSize: 11 }} />
                                        <YAxis dataKey="supplier_name" type="category" width={140} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v) => fmtMoney(v)} />
                                        <Bar dataKey="total_cost" fill="#E9C46A" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* Supplier performance table */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader><CardTitle className="text-sm">{t('entities:reports.purchase.supplier_performance', { defaultValue: 'Supplier performance' })}</CardTitle></CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('entities:suppliers.title')}</TableHead>
                                        <TableHead className="text-right">{t('entities:batches.dialog.fields.qty_received')}</TableHead>
                                        <TableHead className="text-right">{t('entities:reports.purchase.kpi.batches', { defaultValue: 'Batches' })}</TableHead>
                                        <TableHead className="text-right">{t('entities:reports.purchase.total_cost', { defaultValue: 'Total Cost' })}</TableHead>
                                        <TableHead className="text-right">{t('entities:reports.purchase.kpi.recalls', { defaultValue: 'Recalls' })}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.supplier_performance.map((s) => (
                                        <TableRow key={s.supplier_id || s.supplier_name}>
                                            <TableCell className="font-medium">{s.supplier_name}</TableCell>
                                            <TableCell className="text-right font-mono">{s.total_qty}</TableCell>
                                            <TableCell className="text-right">{s.batch_count}</TableCell>
                                            <TableCell className="text-right font-semibold">{fmtMoney(s.total_cost)}</TableCell>
                                            <TableCell className="text-right">
                                                {s.recalls > 0 ? <Badge className="bg-[#E76F51]/20 text-[#E76F51]">{s.recalls}</Badge> : <span className="text-gray-400">—</span>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    {/* Detail */}
                    <Card className="border-0 shadow-sm">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-sm">{t('entities:reports.purchase.purchase_history', { defaultValue: 'Purchase history' })} ({total})</CardTitle>
                            <div className="flex items-center gap-2 no-print">
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={14} /></Button>
                                <span className="text-xs text-gray-500">{page} / {totalPages}</span>
                                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight size={14} /></Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {data.rows.length === 0 ? <ReportEmpty message={t('entities:reports.empty')} /> : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('entities:reports.columns.date')}</TableHead>
                                                <TableHead>{t('entities:batches.columns.batch')}</TableHead>
                                                <TableHead>{t('entities:products.columns.name')}</TableHead>
                                                <TableHead>{t('entities:suppliers.title')}</TableHead>
                                                <TableHead className="text-right">{t('entities:batches.dialog.fields.qty_received')}</TableHead>
                                                {showCost && <TableHead className="text-right">{t('entities:products.dialog.cost_price')}</TableHead>}
                                                {showCost && <TableHead className="text-right">{t('entities:reports.purchase.total_cost', { defaultValue: 'Total Cost' })}</TableHead>}
                                                <TableHead>{t('entities:batches.columns.expiry')}</TableHead>
                                                <TableHead>{t('common:common.status')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.rows.map((r) => (
                                                <TableRow key={r.id} data-testid={`purchase-row-${r.batch_number}`}>
                                                    <TableCell className="text-xs">{r.received_at}</TableCell>
                                                    <TableCell className="font-mono text-xs">{r.batch_number}</TableCell>
                                                    <TableCell>
                                                        <div className="font-medium text-sm">{r.product_name}</div>
                                                        <div className="text-[10px] font-mono text-gray-400">{r.sku}</div>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{r.supplier_name || '-'}</TableCell>
                                                    <TableCell className="text-right font-mono">{r.quantity_received}</TableCell>
                                                    {showCost && <TableCell className="text-right">{fmtMoney(r.cost_price)}</TableCell>}
                                                    {showCost && <TableCell className="text-right font-semibold">{fmtMoney(r.total_cost)}</TableCell>}
                                                    <TableCell className="text-xs">{r.expiry_date}</TableCell>
                                                    <TableCell>{statusBadge(r.status)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default PurchaseReport;
